import { useState, useEffect, useCallback } from 'react';
import { Card, Button, LoadingSpinner } from '../components/Layout';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Records to review: warnings and incident records waiting for the employee's
// acknowledgement.
//
// - Never blocking. Nothing here stops anyone from working; opening this page is
//   the employee's choice. An acknowledgement obtained by preventing work is
//   coercion, not acknowledgement.
// - Nothing is shown until the employee re-enters their password. Shop-floor
//   tablets may be shared; the badge shows only a count. The unlock lives in
//   memory only and expires after 10 minutes or when they leave this page.
// - "Acknowledge and dispute" exists because acknowledgement without the
//   ability to disagree is just a receipt.

interface WarningRecord {
  kind: 'warning';
  id: string;
  warningType: string;
  reason: string;
  expectation: string | null;
  consequence: string | null;
  issuedDate: string;
  incidentDate: string | null;
  activeUntil: string | null;
  issuedByName: string | null;
  witnessName: string | null;
}

interface IncidentRecord {
  kind: 'infraction';
  id: string;
  type: string;
  incidentDate: string | null;
  recordedDate: string;
  description: string | null;
  orderNumber: string | null;
  recordedByName: string | null;
  witnessName: string | null;
}

type RecordItem = WarningRecord | IncidentRecord;

const WARNING_TITLES: Record<string, string> = {
  verbal: 'Documented verbal warning',
  written: 'Written warning',
  final: 'Final written warning',
  suspension: 'Suspension',
};

// Calendar dates arrive as YYYY-MM-DD. Build them as local dates: new Date('2026-09-20')
// is UTC midnight, which is the previous evening in Central.
function formatDate(iso: string | null): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

class LockedError extends Error {}

async function request(path: string, unlockToken: string | null, init: RequestInit = {}) {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(unlockToken ? { 'X-Records-Unlock': unlockToken } : {}),
      ...init.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = Array.isArray(body.message) ? body.message.join('; ') : body.message || 'Something went wrong';
    if (res.status === 403) throw new LockedError(message);
    throw new Error(message);
  }
  return res.json();
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-white mt-0.5 whitespace-pre-wrap">{children}</div>
    </div>
  );
}

export function RecordsPage() {
  const [unlockToken, setUnlockToken] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState('');

  const [items, setItems] = useState<RecordItem[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [selected, setSelected] = useState<RecordItem | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState<'ack' | 'dispute' | null>(null);
  const [actionError, setActionError] = useState('');
  const [done, setDone] = useState<string | null>(null);

  const lock = useCallback((message?: string) => {
    setUnlockToken(null);
    setItems(null);
    setSelected(null);
    setComment('');
    setUnlockError(message || '');
  }, []);

  // The unlock expires server-side after 10 minutes; lock the screen then too.
  useEffect(() => {
    if (!unlockToken) return;
    const t = setTimeout(() => lock('Locked after 10 minutes. Enter your password again to continue.'), 10 * 60 * 1000);
    return () => clearTimeout(t);
  }, [unlockToken, lock]);

  const load = useCallback(async (token: string) => {
    setLoadError('');
    try {
      const data = await request('/discipline/my-records', token);
      setItems([...(data.infractions || []), ...(data.warnings || [])]);
    } catch (err: any) {
      if (err instanceof LockedError) lock(err.message);
      else setLoadError(err.message);
    }
  }, [lock]);

  const unlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;
    setUnlocking(true);
    setUnlockError('');
    try {
      const { unlockToken: token } = await request('/discipline/my-records/unlock', null, {
        method: 'POST',
        body: JSON.stringify({ password }),
      });
      setPassword('');
      setUnlockToken(token);
      await load(token);
    } catch (err: any) {
      setUnlockError(err.message);
    } finally {
      setUnlocking(false);
    }
  };

  const respond = async (dispute: boolean) => {
    if (!selected || !unlockToken) return;
    if (dispute && !comment.trim()) {
      setActionError('To dispute, write what you see differently in the comment box first.');
      return;
    }
    setSubmitting(dispute ? 'dispute' : 'ack');
    setActionError('');
    try {
      const path = selected.kind === 'infraction'
        ? `/discipline/my-records/infractions/${selected.id}/acknowledge`
        : `/discipline/my-records/warnings/${selected.id}/acknowledge`;
      await request(path, unlockToken, { method: 'POST', body: JSON.stringify({ comment, dispute }) });
      setDone(dispute
        ? 'Recorded. Your comment is kept with the record and your dispute will be reviewed.'
        : 'Recorded. Thank you.');
      setSelected(null);
      setComment('');
      await load(unlockToken);
    } catch (err: any) {
      if (err instanceof LockedError) lock(err.message);
      else setActionError(err.message);
    } finally {
      setSubmitting(null);
    }
  };

  // ---- Locked: password first, nothing about the records shown -------------
  if (!unlockToken) {
    return (
      <div className="max-w-md mx-auto space-y-4">
        <Card>
          <h1 className="text-2xl font-bold text-white">Records to review</h1>
          <p className="text-gray-300 mt-2">
            Your records are only shown after you enter your password, in case this screen is shared.
          </p>
          <form onSubmit={unlock} className="mt-4 space-y-3">
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              className="w-full px-4 py-3 rounded-lg bg-gray-900 border border-gray-600 text-white text-lg"
              autoFocus
            />
            {unlockError && <div className="text-red-400 text-sm">{unlockError}</div>}
            <Button onClick={() => {}} disabled={unlocking || !password} className="w-full">
              {unlocking ? 'Checking…' : 'View my records'}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  if (items === null) {
    return (
      <div className="flex flex-col items-center gap-3 p-8">
        {loadError ? <div className="text-red-400">{loadError}</div> : <LoadingSpinner />}
      </div>
    );
  }

  // ---- One record ------------------------------------------------------------
  if (selected) {
    const isIncident = selected.kind === 'infraction';
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <button onClick={() => { setSelected(null); setActionError(''); }} className="text-yellow-400 text-sm">← Back to list</button>
        <Card>
          <div className="text-xs uppercase tracking-wide text-gray-400">{isIncident ? 'Incident record' : 'Warning'}</div>
          <h1 className="text-2xl font-bold text-white mt-1">
            {isIncident ? (selected as IncidentRecord).type : WARNING_TITLES[(selected as WarningRecord).warningType] || 'Warning'}
          </h1>
          <p className="text-gray-300 italic mt-2">
            {isIncident
              ? 'This is a record that an incident occurred. It is not a disciplinary action.'
              : 'This is a formal record of a discussion about work performance or conduct.'}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
            {isIncident ? (
              <>
                <Field label="Date of incident">{formatDate((selected as IncidentRecord).incidentDate)}</Field>
                <Field label="Date recorded">{formatDate((selected as IncidentRecord).recordedDate)}</Field>
                <Field label="Recorded by">{(selected as IncidentRecord).recordedByName}</Field>
                <Field label="Witness">{(selected as IncidentRecord).witnessName}</Field>
                <Field label="Order number">{(selected as IncidentRecord).orderNumber}</Field>
              </>
            ) : (
              <>
                <Field label="Date of this notice">{formatDate((selected as WarningRecord).issuedDate)}</Field>
                <Field label="Date of incident">{formatDate((selected as WarningRecord).incidentDate) || 'Not specified'}</Field>
                <Field label="Issued by">{(selected as WarningRecord).issuedByName}</Field>
                <Field label="Witness">{(selected as WarningRecord).witnessName}</Field>
                <Field label="Active until">{(selected as WarningRecord).activeUntil ? formatDate((selected as WarningRecord).activeUntil) : 'No expiration'}</Field>
              </>
            )}
          </div>

          <div className="mt-5 space-y-4">
            {isIncident ? (
              <Field label="What happened">
                {(selected as IncidentRecord).description?.trim() || 'No description was recorded.'}
              </Field>
            ) : (
              <>
                <Field label="Reason">{(selected as WarningRecord).reason}</Field>
                <Field label="Expectation going forward">{(selected as WarningRecord).expectation}</Field>
                <Field label="Consequence if not corrected">{(selected as WarningRecord).consequence}</Field>
              </>
            )}
          </div>
        </Card>

        <Card>
          <label className="block text-white font-semibold">Your comments</label>
          <p className="text-gray-400 text-sm mt-1">
            {isIncident
              ? 'If you see this differently, write it here. It will be kept with the record.'
              : 'You may write anything you wish to add. It will be kept with the record.'}
          </p>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            className="w-full mt-3 px-3 py-2 rounded-lg bg-gray-900 border border-gray-600 text-white"
          />
          <p className="text-gray-400 text-sm mt-3">
            Acknowledging confirms this was shared with you. It does not mean you agree. If you disagree, write why above and choose <span className="text-white font-medium">Acknowledge and dispute</span>. Your dispute will be reviewed.
          </p>
          {actionError && <div className="text-red-400 text-sm mt-3">{actionError}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <Button variant="success" size="lg" disabled={!!submitting} onClick={() => respond(false)}>
              {submitting === 'ack' ? 'Saving…' : 'Acknowledge'}
            </Button>
            <Button variant="secondary" size="lg" disabled={!!submitting} onClick={() => respond(true)}>
              {submitting === 'dispute' ? 'Saving…' : 'Acknowledge and dispute'}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // ---- List ------------------------------------------------------------------
  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Records to review</h1>
        <button onClick={() => lock()} className="text-sm text-gray-400 hover:text-white">Lock</button>
      </div>
      {done && <div className="bg-green-900/40 border border-green-700 text-green-200 rounded-lg px-4 py-3 text-sm">{done}</div>}
      {items.length === 0 ? (
        <Card><p className="text-gray-300 text-center py-6">Nothing to review.</p></Card>
      ) : (
        items.map((item) => (
          <Card key={`${item.kind}-${item.id}`} onClick={() => { setSelected(item); setDone(null); }}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-400">{item.kind === 'infraction' ? 'Incident record' : 'Warning'}</div>
                <div className="text-white font-semibold mt-0.5">
                  {item.kind === 'infraction' ? item.type : WARNING_TITLES[item.warningType] || 'Warning'}
                </div>
                <div className="text-gray-400 text-sm">
                  {item.kind === 'infraction' ? formatDate(item.incidentDate || item.recordedDate) : formatDate(item.issuedDate)}
                </div>
              </div>
              <span className="text-yellow-400 text-sm">Review →</span>
            </div>
          </Card>
        ))
      )}
    </div>
  );
}

export default RecordsPage;
